import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  PreSalesBookingChannel,
  PreSalesCancellationReason,
  PreSalesQuestion,
  PreSalesSubmitFormResponse,
} from '../../../dto/model';
import {
  evaluatePreSalesQuestionVisible,
  isPreSalesAnswerEmpty,
  PreSalesAnswers,
} from '../../../utils/pre-sales-questionnaire-evaluator';
import type { PreSalesCustomerInfoPatch } from './pre-sales-questionnaire-types';

dayjs.extend(customParseFormat);

export enum PreSalesQuestionType {
  Text = 'text',
  Radio = 'radio',
  Date = 'date',
  Select = 'select',
  Number = 'number',
  MultiSelect = 'multiSelect',
}

type PreSalesSelectOption = {
  value: string;
  label: string;
};

export type ZeegBookingCalendarInfo = {
  link: string;
  ownerSlug?: string;
  eventTypeSlug?: string;
  /** Channel PreSales assigned to this link; travels with the link when switching calendars. */
  channel?: PreSalesBookingChannel;
};

export type ZeegBookingLinksInfo = {
  primary: ZeegBookingCalendarInfo;
  default?: ZeegBookingCalendarInfo;
};

export const PRE_SALES_DATE_FORMAT = 'DD.MM.YYYY';

export const getPreSalesQuestionElementId = (questionKey: string) =>
  `bp-presales-question-${encodeURIComponent(questionKey)}`;

export const scrollToPreSalesQuestion = (questionKey: string) => {
  if (typeof document === 'undefined') return;

  const questionElement = document.getElementById(
    getPreSalesQuestionElementId(questionKey),
  );
  if (!questionElement) return;

  questionElement.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  const control = questionElement.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button:not([disabled]), [role="combobox"]:not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])',
  );
  (control ?? questionElement).focus({ preventScroll: true });
};

export type PreSalesQuestionnaireProgress = {
  answered: number;
  total: number;
  percent: number;
};

export const getPreSalesQuestionnaireProgress = (
  visibleQuestions: PreSalesQuestion[],
  answers: PreSalesAnswers,
): PreSalesQuestionnaireProgress => {
  const total = visibleQuestions.length;
  const answered = visibleQuestions.filter(
    (question) => !isPreSalesAnswerEmpty(answers[question.questionKey]),
  ).length;

  return {
    answered,
    total,
    percent: total === 0 ? 0 : Math.round((answered / total) * 100),
  };
};

export const getPreSalesFaqContent = (question: PreSalesQuestion) =>
  question.additionalInfo?.faqContent?.trim() ?? '';

const toSelectOptions = (value: unknown): PreSalesSelectOption[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((option) => {
      if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
        const text = String(option);
        return { value: text, label: text };
      }

      if (!option || typeof option !== 'object') return null;
      const record = option as Record<string, unknown>;
      const value = record.value ?? record.key ?? record.id ?? record.label ?? record.text;
      const label = record.label ?? record.text ?? record.name ?? value;
      if (value === undefined || label === undefined) return null;
      return { value: String(value), label: String(label) };
    })
    .filter((option): option is PreSalesSelectOption => Boolean(option));
};

export const getPreSalesQuestionOptions = (question: PreSalesQuestion): PreSalesSelectOption[] => {
  const directOptions = toSelectOptions(question.options);
  if (directOptions.length > 0) return directOptions;

  const valuesOptions = toSelectOptions(question.values);
  if (valuesOptions.length > 0) return valuesOptions;

  return toSelectOptions(question.items);
};

export const toPreSalesStringArray = (answer: PreSalesAnswers[string]): string[] => {
  if (Array.isArray(answer)) return answer.map(String);
  if (answer === undefined) return [];
  return [String(answer)];
};

const PRE_SALES_RENOVATION_OPTIONS = {
  newFloorHeating: 'Einbau einer Fußbodenheizung (nur wenn in mehr als einem Raum)',
  insulationRoof: 'Dämmung des Dachs/Dachbodens',
  insulationFacade: 'Dämmung der Fassaden',
  replaceWindows: 'Austausch der Fenster',
} as const;

export const applyPreSalesDerivedAnswers = (
  lastChangedQuestionKey: string,
  answers: PreSalesAnswers,
): PreSalesAnswers => {
  if (lastChangedQuestionKey !== 'home_improvement_impact_heating') {
    return answers;
  }

  const renovations = Array.isArray(answers.home_improvement_impact_heating)
    ? answers.home_improvement_impact_heating
    : [];
  const updates: PreSalesAnswers = {};
  const currentYear = new Date().getFullYear();

  if (renovations.includes(PRE_SALES_RENOVATION_OPTIONS.insulationRoof)) {
    updates.insulated_roof = 'Ja';
    updates.insulated_roof_year = currentYear;
  } else if (answers.insulated_roof) {
    updates.insulated_roof = undefined;
    updates.insulated_roof_year = undefined;
  }

  if (renovations.includes(PRE_SALES_RENOVATION_OPTIONS.insulationFacade)) {
    updates.insulated_facade = 'Ja';
    updates.insulated_facade_year = currentYear;
  } else if (answers.insulated_facade) {
    updates.insulated_facade = undefined;
    updates.insulated_facade_year = undefined;
  }

  if (renovations.includes(PRE_SALES_RENOVATION_OPTIONS.replaceWindows)) {
    updates.isolated_windows = 'Ja';
  } else if (answers.isolated_windows) {
    updates.isolated_windows = undefined;
  }

  if (renovations.includes(PRE_SALES_RENOVATION_OPTIONS.newFloorHeating)) {
    updates.additional_heating_circuits_planned = 'Ja';
    updates.additional_floor_heating_distributors_planned = 'Ja';
  } else if (answers.additional_heating_circuits_planned === 'Ja') {
    updates.additional_heating_circuits_planned = 'Nein';
    updates.additional_floor_heating_distributors_planned = 'Nein';
  }

  return Object.keys(updates).length === 0
    ? answers
    : { ...answers, ...updates };
};

export const parsePreSalesDateAnswer = (answer: string) => {
  if (!answer) return undefined;

  const parsedDisplayDate = dayjs(answer, PRE_SALES_DATE_FORMAT, true);
  if (parsedDisplayDate.isValid()) return parsedDisplayDate;

  const parsedIsoDate = dayjs(answer);
  return parsedIsoDate.isValid() ? parsedIsoDate : undefined;
};

export const getStablePreSalesAnswersKey = (answers: PreSalesAnswers) =>
  JSON.stringify(
    Object.keys(answers)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = answers[key];
        return acc;
      }, {}),
  );

export const serializePreSalesAnswers = (answers: PreSalesAnswers): Record<string, string> =>
  Object.fromEntries(
    Object.entries(answers)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : String(value)]),
  );

// Hidden answers are replaced with their defaults on every evaluation, including after
// visibility changes cleared a previously entered value. Questions without defaults are omitted.
export const serializePreSalesQualificationAnswers = (
  questions: PreSalesQuestion[],
  answers: PreSalesAnswers,
): Array<{ questionKey: string; answer: string }> => questions.flatMap((question) => {
  const visible = evaluatePreSalesQuestionVisible(question, answers);
  const value = visible
    ? answers[question.questionKey] ?? question.defaultValue
    : question.defaultValue;
  if (value == null) return [];
  return [{
    questionKey: question.questionKey,
    answer: Array.isArray(value) ? value.join(',') : String(value),
  }];
});

export type ClearedPreSalesAnswers = {
  answers: PreSalesAnswers;
  clearedQuestionKeys: string[];
};

const PRE_SALES_CUSTOMER_INFO_FIELDS = {
  street: 'street',
  housenumber: 'houseNumber',
  plz: 'zipcode',
  city: 'city',
} as const;

export const getPreSalesCustomerInfoPatch = (
  questionKey: string,
  value: PreSalesAnswers[string],
): PreSalesCustomerInfoPatch | null => {
  const caseInfoField = PRE_SALES_CUSTOMER_INFO_FIELDS[
    questionKey as keyof typeof PRE_SALES_CUSTOMER_INFO_FIELDS
  ];

  if (!caseInfoField || value === undefined || Array.isArray(value)) {
    return null;
  }

  return { [caseInfoField]: String(value) };
};

const getVisiblePreSalesQuestionKeys = (
  questions: PreSalesQuestion[],
  answers: PreSalesAnswers,
) =>
  new Set(
    questions
      .filter((question) => evaluatePreSalesQuestionVisible(question, answers))
      .map((question) => question.questionKey),
  );

export const clearNewlyHiddenPreSalesAnswers = (
  questions: PreSalesQuestion[],
  previousAnswers: PreSalesAnswers,
  nextAnswers: PreSalesAnswers,
): ClearedPreSalesAnswers => {
  const previouslyVisibleKeys = getVisiblePreSalesQuestionKeys(
    questions,
    previousAnswers,
  );
  const clearedQuestionKeys = new Set<string>();
  let clearedAnswers = nextAnswers;

  while (true) {
    const nextVisibleKeys = getVisiblePreSalesQuestionKeys(
      questions,
      clearedAnswers,
    );
    const newlyHiddenKeys: string[] = [];
    previouslyVisibleKeys.forEach((questionKey) => {
      if (
        !nextVisibleKeys.has(questionKey)
        && !clearedQuestionKeys.has(questionKey)
      ) {
        newlyHiddenKeys.push(questionKey);
      }
    });

    if (newlyHiddenKeys.length === 0) {
      break;
    }

    for (const questionKey of newlyHiddenKeys) {
      clearedQuestionKeys.add(questionKey);
      if (Object.prototype.hasOwnProperty.call(clearedAnswers, questionKey)) {
        if (clearedAnswers === nextAnswers) {
          clearedAnswers = { ...nextAnswers };
        }
        delete clearedAnswers[questionKey];
      }
    }
  }

  return {
    answers: clearedAnswers,
    clearedQuestionKeys: Array.from(clearedQuestionKeys),
  };
};

const PRE_SALES_FORCED_SUBMIT_ANSWERS: PreSalesAnswers = {
  heating_owner: 'Ja',
  heating_temperature: '20',
  floor_convector: 'Nein',
  rainshower: 'Nein',
  bathtub: 'Ja',
  heatpump_location: 'Ja',
  cable_distance: 'Unter 25 Meter',
  water_boiler_room: 'Nein',
  bathrooms: 2,
  mobility_costs: 150,
  house_construction: 'Nein',
  house_restoration: 'Nein',
  single_pipe: 'Nicht sicher',
  car_type: 'Elektroauto',
  rent: 'Nein',
  swimming_pool: 'Nein',
  heatpump_space: '3 oder mehr',
  heatingroom_height: 'Nicht Sicher',
  heating_circuits: 'Nicht sicher',
};

const getHasRadiatorsAnswer = (heaterType: unknown) => {
  switch (String(heaterType ?? '')) {
    case 'Weder Noch':
    case 'Fußbodenheizung':
      return 'Nein';
    default:
      return 'Ja';
  }
};

const getHasUnderfloorHeatingAnswer = (heaterType: unknown, plannedRenovations: unknown) => {
  if (
    Array.isArray(plannedRenovations)
    && plannedRenovations.includes(PRE_SALES_RENOVATION_OPTIONS.newFloorHeating)
  ) {
    return 'Ja';
  }

  return ['Beides', 'Fußbodenheizung'].includes(String(heaterType ?? '')) ? 'Ja' : 'Nein';
};

const toPreSalesSubmitAnswer = (answer: PreSalesAnswers[string] | null) =>
  answer == null ? '' : String(answer);

export const serializePreSalesSubmitAnswers = (
  questions: PreSalesQuestion[],
  answers: PreSalesAnswers,
): Array<Record<string, unknown>> => {
  // Keep parity with the Heat Pre-Sales Tool: these legacy defaults intentionally
  // overwrite form values before the submission payload is built.
  const submitAnswers: PreSalesAnswers = {
    ...answers,
    ...PRE_SALES_FORCED_SUBMIT_ANSWERS,
  };

  let serializedAnswers = questions.map((question) => ({
    questionKey: question.questionKey,
    answer: toPreSalesSubmitAnswer(
      submitAnswers[question.questionKey] ?? question.defaultValue ?? null,
    ),
  }));

  if (submitAnswers.living_area != null && submitAnswers.heated_area == null) {
    serializedAnswers = [
      ...serializedAnswers.filter(({ questionKey }) => questionKey !== 'heated_area'),
      {
        questionKey: 'heated_area',
        answer: toPreSalesSubmitAnswer(submitAnswers.living_area),
      },
    ];
  }

  const specialQuestionKeys = new Set([
    'has_radiators',
    'floor_heating',
    'water_heater',
    'pv_roof_shape',
    'pv_roof_material',
  ]);
  const waterHeater = submitAnswers.water_heater;
  const pvRoofShape = submitAnswers.pv_roof_shape;
  const pvRoofMaterial = submitAnswers.pv_roof_material;

  return [
    ...serializedAnswers.filter(({ questionKey }) => !specialQuestionKeys.has(questionKey)),
    {
      questionKey: 'has_radiators',
      answer: getHasRadiatorsAnswer(submitAnswers.heater_type),
    },
    {
      questionKey: 'floor_heating',
      answer: getHasUnderfloorHeatingAnswer(
        submitAnswers.heater_type,
        submitAnswers.home_improvement_impact_heating,
      ),
    },
    {
      questionKey: 'water_heater',
      answer: typeof waterHeater === 'string' ? waterHeater : 'Nein',
    },
    {
      questionKey: 'pv_roof_shape',
      answer: typeof pvRoofShape === 'string' ? pvRoofShape : 'Sonstiges',
    },
    {
      questionKey: 'pv_roof_material',
      answer: typeof pvRoofMaterial === 'string' ? pvRoofMaterial : 'Sonstiges',
    },
  ];
};

export type PreSalesCancellationProjection = {
  reasons: PreSalesCancellationReason[];
  messages: string[];
  questionKeys: string[];
};

export const formatPreSalesCancellationReason = (reason: PreSalesCancellationReason): string => {
  const company = reason.company?.trim();
  if (!['HP', 'WP'].includes(reason.product.toUpperCase()) || !company || company.toLowerCase() === 'all') {
    return reason.message;
  }
  const companyLabel = company.toLowerCase() === 'bosch' ? 'Bosch'
    : company.toLowerCase() === 'enpal' ? 'Enpal' : company;
  return `${companyLabel}: ${reason.message}`;
};

export const getPreSalesCancellationProjection = (
  cancellationReasons: PreSalesCancellationReason[],
  product: string,
): PreSalesCancellationProjection => {
  const reasons = cancellationReasons.filter((reason) => reason.product === product);
  return {
    reasons,
    messages: Array.from(new Set(reasons.filter((reason) => reason.message).map(formatPreSalesCancellationReason))),
    questionKeys: Array.from(new Set(reasons.flatMap((reason) => reason.questionKeys))),
  };
};

const resolveZeegBookingCalendarInfo = (
  link?: string | null,
  ownerSlug?: string | null,
  eventTypeSlug?: string | null,
  channel?: PreSalesBookingChannel | null,
): ZeegBookingCalendarInfo | null => {
  if (!link) return null;

  return {
    link,
    ownerSlug: ownerSlug ?? undefined,
    eventTypeSlug: eventTypeSlug ?? undefined,
    channel: channel ?? undefined,
  };
};

export const resolveZeegBookingLinksInfo = (
  bookingLinks: PreSalesSubmitFormResponse['bookingLinks'],
): ZeegBookingLinksInfo | null => {
  if (!bookingLinks) return null;

  const primary = resolveZeegBookingCalendarInfo(
    bookingLinks.primary,
    bookingLinks.primaryOwnerSlug,
    bookingLinks.primaryEventTypeSlug,
    bookingLinks.primaryChannel,
  );
  const defaultCalendar = resolveZeegBookingCalendarInfo(
    bookingLinks.default,
    bookingLinks.defaultOwnerSlug,
    bookingLinks.defaultEventTypeSlug,
    bookingLinks.defaultChannel,
  );

  if (!primary) {
    return defaultCalendar ? { primary: defaultCalendar } : null;
  }

  const hasDefaultRouting = Boolean(
    defaultCalendar?.ownerSlug
    && defaultCalendar.eventTypeSlug,
  );

  return {
    primary,
    ...(hasDefaultRouting && defaultCalendar ? { default: defaultCalendar } : {}),
  };
};
